import type { AuthAdapter, AuthState, SignInInput, SignUpInput } from './types';
import { LocalAuthAdapter } from './local-auth-adapter';

const loopback = (host: string) => ['127.0.0.1', 'localhost', '::1'].includes(host);

let enterpriseScript: Promise<void> | undefined;
export function loadEnterpriseScript(): Promise<void> {
  const enterprise = () => (globalThis as typeof globalThis & {
    grecaptcha?: { enterprise?: { ready?: (callback: () => void) => void; render?: unknown } };
  }).grecaptcha?.enterprise;
  const loaded = () => typeof enterprise()?.ready === 'function' && typeof enterprise()?.render === 'function';
  if (loaded()) return Promise.resolve();
  if (enterpriseScript) return enterpriseScript;
  enterpriseScript = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    // Google's documented equivalent host; App Check still verifies the same key/token.
    script.src = 'https://www.recaptcha.net/recaptcha/enterprise.js?render=explicit';
    script.async = true;
    const fail = () => {
      clearTimeout(timer);
      script.onload = null;
      script.onerror = null;
      script.remove();
      reject(Object.assign(new Error('Security script unavailable'), { code: 'appCheck/network-request-failed' }));
    };
    const timer = setTimeout(fail, 15_000);
    script.onerror = fail;
    script.onload = () => {
      const api = enterprise();
      if (typeof api?.ready !== 'function') return fail();
      api.ready(() => {
        if (!loaded()) return fail();
        clearTimeout(timer);
        script.onload = null;
        script.onerror = null;
        resolve();
      });
    };
    document.head.appendChild(script);
  });
  return enterpriseScript;
}

/** Bound both module initialization and each account transition; never admit on timeout. */
export function observeDeferredAuth(
  delegate: Promise<AuthAdapter>,
  listener: (state: AuthState) => void,
) {
  let alive = true;
  let unsubscribe = () => {};
  let timer: ReturnType<typeof setTimeout>;
  const arm = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (alive) listener({ state: 'session-unavailable', code: 'IDENTITY-SESSION-TIMEOUT' });
    }, 30_000);
  };
  arm();
  void delegate.then((adapter) => {
    if (!alive) return;
    unsubscribe = adapter.observe((state) => {
      if (!alive) return;
      if (state.state === 'loading') arm();
      else clearTimeout(timer);
      listener(state);
    });
  }).catch(() => {
    clearTimeout(timer);
    if (alive) listener({ state: 'session-unavailable', code: 'IDENTITY-INITIALIZATION-FAILED' });
  });
  return () => {
    alive = false;
    clearTimeout(timer);
    unsubscribe();
  };
}
class LazyFirebaseEmulatorAuthAdapter implements AuthAdapter {
  private readonly delegate: Promise<AuthAdapter>;
  private resolvedDelegate: AuthAdapter | null = null;
  constructor() {
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '';
    const firestoreHost = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST ?? '127.0.0.1';
    const functionsHost = import.meta.env.VITE_FUNCTIONS_EMULATOR_HOST ?? '127.0.0.1';
    if (!projectId.startsWith('demo-') || !loopback(firestoreHost) || !loopback(functionsHost))
      throw new Error('Identity Auth adapter accepts demo-* and loopback emulators only');
    this.delegate = import('@kop/operations-firebase-adapter').then(
      (module) =>
        module.createIdentityFirebaseEmulatorAuthAdapter({
          projectId,
          firestore: {
            host: firestoreHost,
            port: Number(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT ?? 8080),
          },
          functions: {
            host: functionsHost,
            port: Number(import.meta.env.VITE_FUNCTIONS_EMULATOR_PORT ?? 5001),
          },
          auth: {
            host: import.meta.env.VITE_AUTH_EMULATOR_HOST ?? '127.0.0.1',
            port: Number(import.meta.env.VITE_AUTH_EMULATOR_PORT ?? 9099),
          },
          storage: {
            host: import.meta.env.VITE_STORAGE_EMULATOR_HOST ?? '127.0.0.1',
            port: Number(import.meta.env.VITE_STORAGE_EMULATOR_PORT ?? 9199),
          },
        }) as unknown as AuthAdapter,
    ).then((adapter) => {
      this.resolvedDelegate = adapter;
      return adapter;
    },
    );
  }
  observe(listener: (state: AuthState) => void) {
    return observeDeferredAuth(this.delegate, listener);
  }
  observeEntitlement(listener: (identityId: string) => void) {
    let unsubscribe = () => {};
    let alive = true;
    void this.delegate.then((value) => {
      if (alive) unsubscribe = value.observeEntitlement?.(listener) ?? unsubscribe;
    }).catch(() => { /* The state observer presents initialization failures. */ });
    return () => {
      alive = false;
      unsubscribe();
    };
  }
  async signIn(input: SignInInput) {
    return (await this.delegate).signIn(input);
  }
  async reauthenticateWithPassword(password: string) {
    const method = (await this.delegate).reauthenticateWithPassword;
    if (!method) throw new Error('AUTH_REAUTH_UNAVAILABLE');
    return method(password);
  }
  consumeFreshAuthentication(identityId: string) {
    return this.resolvedDelegate?.consumeFreshAuthentication?.(identityId) ?? false;
  }
  async executeMobilePasskey(input: Readonly<Record<string, unknown>>) {
    const method = (await this.delegate).executeMobilePasskey;
    if (!method) throw new Error('PASSKEY_UNAVAILABLE');
    return method(input);
  }
  async signUp(input: SignUpInput) {
    return (await this.delegate).signUp(input);
  }
  async consumeRegistrationInvite() {
    return (await this.delegate).consumeRegistrationInvite();
  }
  async loadRegistrationOptions(email: string, siteCandidateId?: string | null) {
    return (await this.delegate).loadRegistrationOptions(email, siteCandidateId);
  }
  async completeRegistration(input: Parameters<AuthAdapter['completeRegistration']>[0]) {
    return (await this.delegate).completeRegistration(input);
  }
  async loadOnboardingOptions() {
    return (await this.delegate).loadOnboardingOptions();
  }
  async saveOnboardingDraft(input: Parameters<AuthAdapter['saveOnboardingDraft']>[0]) {
    return (await this.delegate).saveOnboardingDraft(input);
  }
  async completeOnboarding(input: Parameters<AuthAdapter['completeOnboarding']>[0]) {
    return (await this.delegate).completeOnboarding(input);
  }
  async loadPersonalSignature(required: boolean) {
    return (await this.delegate).loadPersonalSignature(required);
  }
  async uploadPersonalSignature(file: File, dimensions: { width: number; height: number }) {
    return (await this.delegate).uploadPersonalSignature(file, dimensions);
  }
  async registerPersonalSignature(input: Parameters<AuthAdapter['registerPersonalSignature']>[0]) {
    return (await this.delegate).registerPersonalSignature(input);
  }
  async loadMyInfo() {
    return (await this.delegate).loadMyInfo();
  }
  async updateMyInfo(input: Parameters<AuthAdapter['updateMyInfo']>[0]) {
    return (await this.delegate).updateMyInfo(input);
  }
  async revokePersonalSignature(input: Parameters<AuthAdapter['revokePersonalSignature']>[0]) {
    return (await this.delegate).revokePersonalSignature(input);
  }
  async signOut() {
    return (await this.delegate).signOut();
  }
  async selectScope(scopeId: string) {
    return (await this.delegate).selectScope(scopeId);
  }
  async refreshApproval() {
    return (await this.delegate).refreshApproval();
  }
}

class LazyFirebaseProductionAuthAdapter implements AuthAdapter {
  private readonly delegate: Promise<AuthAdapter>;
  private resolvedDelegate: AuthAdapter | null = null;
  constructor() {
    const config = {
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
      appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
      appCheckSiteKey: import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? '',
    };
    if (Object.values(config).some((value) => value.trim().length === 0))
      throw new Error('Firebase identity environment is incomplete');
    /* The bucket is deliberately outside the completeness check above: the app
     * booted without it before signatures existed, and a missing key must show as
     * a named signature failure rather than a workspace that will not start. */
    const storageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? '';
    this.delegate = Promise.all([
      import('@kop/operations-firebase-adapter'),
      loadEnterpriseScript(),
    ]).then(
      ([module]) =>
        module.createIdentityFirebaseAuthAdapter({
          ...config,
          storageBucket,
        }) as unknown as AuthAdapter,
    ).then((adapter) => {
      this.resolvedDelegate = adapter;
      return adapter;
    },
    );
  }
  observe(listener: (state: AuthState) => void) {
    return observeDeferredAuth(this.delegate, listener);
  }
  observeEntitlement(listener: (identityId: string) => void) {
    let unsubscribe = () => {};
    let alive = true;
    void this.delegate.then((value) => {
      if (alive) unsubscribe = value.observeEntitlement?.(listener) ?? unsubscribe;
    }).catch(() => { /* The state observer presents initialization failures. */ });
    return () => {
      alive = false;
      unsubscribe();
    };
  }
  async signIn(input: SignInInput) {
    return (await this.delegate).signIn(input);
  }
  async reauthenticateWithPassword(password: string) {
    const method = (await this.delegate).reauthenticateWithPassword;
    if (!method) throw new Error('AUTH_REAUTH_UNAVAILABLE');
    return method(password);
  }
  consumeFreshAuthentication(identityId: string) {
    return this.resolvedDelegate?.consumeFreshAuthentication?.(identityId) ?? false;
  }
  async executeMobilePasskey(input: Readonly<Record<string, unknown>>) {
    const method = (await this.delegate).executeMobilePasskey;
    if (!method) throw new Error('PASSKEY_UNAVAILABLE');
    return method(input);
  }
  async signUp(input: SignUpInput) {
    return (await this.delegate).signUp(input);
  }
  async consumeRegistrationInvite() {
    return (await this.delegate).consumeRegistrationInvite();
  }
  async loadRegistrationOptions(email: string, siteCandidateId?: string | null) {
    return (await this.delegate).loadRegistrationOptions(email, siteCandidateId);
  }
  async completeRegistration(input: Parameters<AuthAdapter['completeRegistration']>[0]) {
    return (await this.delegate).completeRegistration(input);
  }
  async loadOnboardingOptions() {
    return (await this.delegate).loadOnboardingOptions();
  }
  async saveOnboardingDraft(input: Parameters<AuthAdapter['saveOnboardingDraft']>[0]) {
    return (await this.delegate).saveOnboardingDraft(input);
  }
  async completeOnboarding(input: Parameters<AuthAdapter['completeOnboarding']>[0]) {
    return (await this.delegate).completeOnboarding(input);
  }
  async loadPersonalSignature(required: boolean) {
    return (await this.delegate).loadPersonalSignature(required);
  }
  async uploadPersonalSignature(file: File, dimensions: { width: number; height: number }) {
    return (await this.delegate).uploadPersonalSignature(file, dimensions);
  }
  async registerPersonalSignature(input: Parameters<AuthAdapter['registerPersonalSignature']>[0]) {
    return (await this.delegate).registerPersonalSignature(input);
  }
  async loadMyInfo() {
    return (await this.delegate).loadMyInfo();
  }
  async updateMyInfo(input: Parameters<AuthAdapter['updateMyInfo']>[0]) {
    return (await this.delegate).updateMyInfo(input);
  }
  async revokePersonalSignature(input: Parameters<AuthAdapter['revokePersonalSignature']>[0]) {
    return (await this.delegate).revokePersonalSignature(input);
  }
  async signOut() {
    return (await this.delegate).signOut();
  }
  async selectScope(scopeId: string) {
    return (await this.delegate).selectScope(scopeId);
  }
  async refreshApproval() {
    return (await this.delegate).refreshApproval();
  }
}

export function createWorkspaceAuthAdapter(): AuthAdapter {
  if (import.meta.env.VITE_AUTH_ADAPTER === 'firebase-emulator')
    return new LazyFirebaseEmulatorAuthAdapter();
  if (import.meta.env.VITE_AUTH_ADAPTER === 'firebase')
    return new LazyFirebaseProductionAuthAdapter();
  if (import.meta.env.VITE_AUTH_ADAPTER === 'local') {
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '';
    const previewEnabled = import.meta.env.VITE_LOCAL_PREVIEW === 'true';
    const hostname = typeof window === 'undefined' ? '' : window.location.hostname;
    if (!previewEnabled || !projectId.startsWith('demo-') || !loopback(hostname)) {
      throw new Error('Local identity preview requires explicit demo-* loopback opt-in');
    }
    return new LocalAuthAdapter(true);
  }
  return new LocalAuthAdapter(false);
}
